import { useState, useCallback, useRef } from 'react';

export type Measurement = {
  textWidth: number;
  textHeight: number;
};

type SvgMeasurement = Measurement & {
  pathLength?: number;
  viewBox?: string;
};

function measurePath(path: SVGPathElement | null) {
  if (!path) {
    return { pathLength: undefined, viewBox: undefined };
  }
  const pathLength = path.getTotalLength();
  const viewBoxObject = path.getBBox();
  const viewBox = viewBoxObject
    ? `${viewBoxObject.x} ${viewBoxObject.y} ${viewBoxObject.width} ${viewBoxObject.height}`
    : undefined;
  return { pathLength, viewBox };
}

function getMeasurement(svgMeasurement: SvgMeasurement | undefined) {
  if (!svgMeasurement) {
    return undefined;
  }
  return {
    textWidth: svgMeasurement.textWidth,
    textHeight: svgMeasurement.textHeight,
  };
}

export function useSvgMeasurement(hasPartialMeasurementData: boolean) {
  const [svgMeasurement, setSvgMeasurement] = useState<SvgMeasurement>();
  const prevNodeRef = useRef<SVGSVGElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const measure = useCallback(
    (node: SVGSVGElement) => {
      const heightMeasureText = node.querySelector(
        '[data-measure-height]',
      ) as SVGTextElement;
      const widthMeasureText = node.querySelector(
        '[data-measure-width]',
      ) as SVGTextElement;

      if (!heightMeasureText || !widthMeasureText) {
        return;
      }

      // Measure
      const textHeight = Math.ceil(heightMeasureText.getBBox().height);
      const textWidth =
        widthMeasureText.getBBox().width +
        Math.abs(widthMeasureText.getBBox().x);

      const path = hasPartialMeasurementData
        ? null
        : (node.querySelector('[data-measure-path]') as SVGPathElement);

      const { pathLength, viewBox } = measurePath(path);

      setSvgMeasurement({
        textWidth,
        textHeight,
        pathLength,
        viewBox,
      });
    },
    [hasPartialMeasurementData],
  );

  const measureRefCallback = useCallback(
    (node: SVGSVGElement | null) => {
      // Skip if same node
      if (node === prevNodeRef.current) {
        return;
      }
      prevNodeRef.current = node;

      if (!node) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        return;
      }
      abortControllerRef.current ??= new AbortController();

      if (document.fonts?.status === 'loaded') {
        // when switching to preview fonts already loaded
        measure(node);
      } else {
        document.fonts?.addEventListener('loadingdone', () => measure(node), {
          once: true,
          signal: abortControllerRef.current.signal,
        });
      }
      // Setup resize listener
      window.addEventListener('resize', () => measure(node), {
        signal: abortControllerRef.current.signal,
      });
    },
    [measure],
  );

  return {
    measureRefCallback,
    measurement: getMeasurement(svgMeasurement),
    pathMeasurement: {
      pathLength: svgMeasurement?.pathLength,
      viewBox: svgMeasurement?.viewBox,
    },
  };
}
